import { useTranslation } from 'react-i18next';
import type { ExpandedState } from '@tanstack/react-table';

import Checkbox from '@/components/atoms/Checkbox';
import { DataTable } from '@/components/organisms/DataTable';
import { useDeleteCage } from '@/domain/cages';

import type { ClientCageProps, ClientLocationProps } from '../Client';

import { useLocationsTableColumns } from './locationsTableColumns';
import type { LocationsTableRow } from './locationsTableTypes';

interface LocationsTableProps {
    locations: ClientLocationProps[];
    expandedLocations: Record<number, boolean>;
    selectedLocations: Record<number, boolean>;
    refetch: () => void;
    onAddCageToLocation: (locationId: number) => void;
    onDeleteCageToLocation: (locationId: number) => void;
    onUpdateLocation: (location: ClientLocationProps) => void;
    onEditCage: (cage: ClientCageProps, location: ClientLocationProps) => void;
    onToggleLocationExpanded: (locationId: number, expanded: boolean) => void;
    onToggleLocationSelected: (locationId: number, selected: boolean) => void;
    onToggleLocationsSelected: (locationIds: number[], selected: boolean) => void;
}

function rowId(row: LocationsTableRow): string {
    return row.kind === 'location' ? `location-${row.location.id}` : `cage-${row.cage.id}`;
}

function subRows(row: LocationsTableRow): LocationsTableRow[] | undefined {
    if (row.kind !== 'location') return undefined;
    return row.location.cages.map((cage) => ({ kind: 'cage', cage, location: row.location }));
}

/** The client's locations, each expandable to show its pens. */
export default function LocationsTable(props: LocationsTableProps) {
    const {
        locations,
        expandedLocations,
        selectedLocations,
        refetch,
        onAddCageToLocation,
        onDeleteCageToLocation,
        onUpdateLocation,
        onEditCage,
        onToggleLocationExpanded,
        onToggleLocationSelected,
        onToggleLocationsSelected,
    } = props;

    const { t } = useTranslation();
    const { mutateAsync: deleteCage } = useDeleteCage({ onSettled: refetch });
    const selectionValues = locations.map((location) => !!selectedLocations[location.id]);
    const allSelected = selectionValues.length > 0 && selectionValues.every((selected) => selected);
    const someSelected = allSelected ? false : selectionValues.some((selected) => selected);

    const selectAll = (
        <Checkbox
            aria-label={t('clientLocations.locationsTable.selectAll')}
            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
            onCheckedChange={() =>
                onToggleLocationsSelected(
                    locations.map((location) => location.id),
                    !someSelected && !allSelected
                )
            }
        />
    );

    const columns = useLocationsTableColumns(
        {
            isExpanded: (id) => !!expandedLocations[id],
            isSelected: (id) => !!selectedLocations[id],
            onToggleExpanded: (id) => onToggleLocationExpanded(id, !expandedLocations[id]),
            onToggleSelected: (id) => onToggleLocationSelected(id, !selectedLocations[id]),
            onAddCageToLocation,
            onDeleteCageToLocation,
            onUpdateLocation,
            onEditCage,
            deleteCage: (cageId) => deleteCage({ cageId }),
        },
        selectAll
    );

    const expanded: ExpandedState = Object.fromEntries(
        locations
            .filter((location) => expandedLocations[location.id])
            .map((location) => [`location-${location.id}`, true])
    );

    return (
        <DataTable
            data={locations.map((location): LocationsTableRow => ({ kind: 'location', location }))}
            columns={columns}
            getRowId={rowId}
            getSubRows={subRows}
            expanded={expanded}
            enableSorting={false}
        />
    );
}
